import React, { useState, useMemo, useEffect } from "react";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useNotifications from "../../utils/hooks/notifications/useNotifications";
import { NotificationType } from "../../utils/api/endpoints/notificationsApi";
import { FiCheck, FiSettings, FiTrash2, FiBell, FiMail } from "react-icons/fi";
import NotificationStatCards from "./components/NotificationStatCards";
import NotificationFilterBar from "./components/NotificationFilterBar";
import NotificationCard from "./components/NotificationCard";
import NotificationGroup from "./components/NotificationGroup";
import BulkActionBar from "./components/BulkActionBar";
import ConfirmationModal from "./components/ConfirmationModal";

const NotificationsPage: React.FC = () => {
  usePageTitle("Notifications Center");

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [activeTab, setActiveTab] = useState<"all" | "updates" | "comments">("all");
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread' | 'priority' | 'review'>('all');

  // Notifications custom hook
  const {
    notifications,
    stats,
    total,
    unreadCount,
    highPriorityCount,
    actionRequiredCount,
    isLoading,
    updateFilters,
    markAsRead,
    markAsUnread,
    markActionClicked,
    deleteSelected,
    markAllAsRead,
    markAllAsUnread,
    deleteAll,
    clearAll,
    filters,
    hasMore
  } = useNotifications({
    limit: 10,
    read: false
  });

  const handleLoadMore = () => {
    updateFilters({ limit: (filters.limit || 10) + 10 });
  };

  // Keep API query filters in sync with UI states
  useEffect(() => {
    const apiFilters: any = {
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      priority: selectedPriority !== "all" ? selectedPriority : undefined,
      read: (selectedFilter !== "all" || showUnreadOnly) ? false : undefined,
      high_priority: selectedFilter === "priority" ? true : undefined,
      action_required: selectedFilter === "review" ? true : undefined,
      sort: sortBy,
      type: activeTab === "comments" ? NotificationType.Comments : undefined
    };

    if (selectedFilter === "unread") {
      apiFilters.read = false;
    }

    updateFilters(apiFilters);
    setSelectedIds(new Set()); // Deselect all when filters change
    setSelectAllMode(false);
  }, [selectedCategory, selectedPriority, showUnreadOnly, sortBy, activeTab, selectedFilter]);

  // Client-side search filtering
  const filteredNotifications = useMemo(() => {
    if (!searchTerm) return notifications;
    const lower = searchTerm.toLowerCase();
    return notifications.filter(n =>
      n.title.toLowerCase().includes(lower) ||
      n.message.toLowerCase().includes(lower)
    );
  }, [notifications, searchTerm]);

  // Split list into main feed and comments group
  const comments = useMemo(() => {
    return filteredNotifications.filter(n => n.type === NotificationType.Comments);
  }, [filteredNotifications]);

  const mainFeed = useMemo(() => {
    return filteredNotifications.filter(n => n.type !== NotificationType.Comments);
  }, [filteredNotifications]);

  // Selection states for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Determine if master checkbox is checked (all loaded items selected)
  const allLoadedSelected = filteredNotifications.length > 0 &&
    filteredNotifications.every(n => selectedIds.has(n.id));

  // Some but not all loaded items selected (indeterminate state)
  const someSelected = selectedIds.size > 0 && !allLoadedSelected;

  const handleSelectToggle = (id: number) => {
    setSelectAllMode(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleMasterCheckboxToggle = () => {
    if (allLoadedSelected || selectAllMode) {
      // Deselect everything
      setSelectedIds(new Set());
      setSelectAllMode(false);
    } else {
      // Select all loaded notifications
      const allIds = new Set(filteredNotifications.map(n => n.id));
      setSelectedIds(allIds);
      setSelectAllMode(false);
    }
  };

  const handleSelectAllGlobal = () => {
    setSelectAllMode(true);
    setSelectedIds(new Set());
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
    setSelectAllMode(false);
  };

  const handleBulkMarkRead = () => {
    if (selectAllMode) {
      markAllAsRead();
    } else {
      const ids = Array.from(selectedIds);
      if (ids.length > 0) {
        markAsRead(ids);
      }
    }
    setSelectedIds(new Set());
    setSelectAllMode(false);
  };

  const handleBulkClear = () => {
    // Show confirmation modal
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (selectAllMode) {
      clearAll();
    } else {
      const ids = Array.from(selectedIds);
      if (ids.length > 0) {
        deleteSelected(ids);
      }
    }
    setSelectedIds(new Set());
    setSelectAllMode(false);
    setShowDeleteConfirm(false);
  };

  const deleteCount = selectAllMode ? total : selectedIds.size;

  return (
    <div className="relative flex h-full flex-col px-8 py-8 w-full max-w-full">
      {/* Top Title & Subheader */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black text-[#010D2C] font-montserrat">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="bg-[#58CC02] text-white text-xs font-black px-2.5 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Stay updated with important activities and updates.
          </p>
        </div>

        {/* Action icons */}
        <div className="flex items-center gap-3">
          <button
            onClick={markAllAsRead}
            title="Mark all as read"
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg text-[#010D2C] hover:text-black font-bold transition-all text-xs duration-200"
          >
            <FiCheck className="w-4 h-4" />
            <span>Mark all read</span>
          </button>
          <button
            onClick={markAllAsUnread}
            title="Mark all as unread"
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg text-[#010D2C] hover:text-black font-bold transition-all text-xs duration-200"
          >
            <FiMail className="w-4 h-4" />
            <span>Mark all unread</span>
          </button>
          <a
            href="/dashboard/settings"
            title="Notification Settings"
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 bg-white hover:bg-gray-50 rounded-lg text-[#010D2C] hover:text-black font-bold transition-all text-xs duration-200"
          >
            <FiSettings className="w-4 h-4" />
            <span>Settings</span>
          </a>
        </div>
      </div>

      {/* Filter bar */}
      <NotificationFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedPriority={selectedPriority}
        onPriorityChange={setSelectedPriority}
        showUnreadOnly={showUnreadOnly}
        onShowUnreadOnlyChange={setShowUnreadOnly}
        sortBy={sortBy}
        onSortByChange={setSortBy}
      />

      {/* Tab Filters */}
      <div className="flex border-b border-gray-200 mb-6 mt-2">
        <button
          onClick={() => {
            setActiveTab("all");
            updateFilters({ limit: 10, offset: 0 });
          }}
          className={`px-5 py-3 text-sm font-bold border-b-2 transition-all duration-200 ${
            activeTab === "all"
              ? "border-[#010D2C] text-[#010D2C]"
              : "border-transparent text-gray-500 hover:text-gray-900"
          }`}
        >
          All Notifications
        </button>
        <button
          onClick={() => {
            setActiveTab("updates");
            updateFilters({ limit: 10, offset: 0 });
          }}
          className={`px-5 py-3 text-sm font-bold border-b-2 transition-all duration-200 ${
            activeTab === "updates"
              ? "border-[#010D2C] text-[#010D2C]"
              : "border-transparent text-gray-500 hover:text-gray-900"
          }`}
        >
          Updates
        </button>
        <button
          onClick={() => {
            setActiveTab("comments");
            updateFilters({ limit: 10, offset: 0 });
          }}
          className={`px-5 py-3 text-sm font-bold border-b-2 transition-all duration-200 ${
            activeTab === "comments"
              ? "border-[#010D2C] text-[#010D2C]"
              : "border-transparent text-gray-500 hover:text-gray-900"
          }`}
        >
          Comments
        </button>
      </div>

      {/* Summary stat cards */}
      <NotificationStatCards
        total={total}
        unread={unreadCount}
        priorityCount={highPriorityCount}
        reviewRequiredCount={actionRequiredCount}
        selectedFilter={selectedFilter}
        onSelectFilter={setSelectedFilter}
      />

      {/* Notification Lists */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#010D2C] mb-4" />
          <span className="text-sm font-semibold">Loading notifications...</span>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
          <FiBell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-bold">No notifications found</p>
          <p className="text-xs text-gray-400 mt-1 font-semibold">
            Try adjusting your search query or filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4 pb-24">
          {/* Master checkbox & Selection Banner */}
          <div className="flex items-center gap-3 px-1">
            <input
              type="checkbox"
              checked={allLoadedSelected || selectAllMode}
              ref={(el) => {
                if (el) el.indeterminate = someSelected && !selectAllMode;
              }}
              onChange={handleMasterCheckboxToggle}
              className="w-4 h-4 rounded text-[#010D2C] border-gray-300 focus:ring-[#010D2C] cursor-pointer accent-[#010D2C]"
            />
            <span className="text-sm font-semibold text-gray-600">
              Select all
            </span>
          </div>

          {/* Selection Banner */}
          {(allLoadedSelected || selectAllMode) && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#010D2C]/[0.04] border border-[#010D2C]/10 rounded-lg text-sm">
              {selectAllMode ? (
                <>
                  <span className="font-bold text-[#010D2C]">
                    All {total} notifications are selected.
                  </span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-[#010D2C] font-bold underline underline-offset-2 hover:text-[#010D2C]/70 transition-colors ml-1"
                  >
                    Clear selection
                  </button>
                </>
              ) : (
                <>
                  <span className="font-semibold text-gray-700">
                    {selectedIds.size} notification{selectedIds.size !== 1 ? "s" : ""} on this page selected.
                  </span>
                  {total > filteredNotifications.length && (
                    <button
                      onClick={handleSelectAllGlobal}
                      className="text-[#010D2C] font-bold underline underline-offset-2 hover:text-[#010D2C]/70 transition-colors ml-1"
                    >
                      Select all {total} notifications
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* All tab - render everything in one unified list (comments mixed in by sort order) */}
          {activeTab === "all" &&
            filteredNotifications.map(notification => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkRead={(id) => markAsRead([id])}
                onMarkUnread={(id) => markAsUnread([id])}
                onDelete={(id) => deleteSelected([id])}
                isSelected={selectedIds.has(notification.id) || selectAllMode}
                onSelectToggle={handleSelectToggle}
                onActionClicked={(id) => markActionClicked([id])}
              />
            ))}

          {/* Updates tab - only non-comment notifications */}
          {activeTab === "updates" &&
            mainFeed.map(notification => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkRead={(id) => markAsRead([id])}
                onMarkUnread={(id) => markAsUnread([id])}
                onDelete={(id) => deleteSelected([id])}
                isSelected={selectedIds.has(notification.id) || selectAllMode}
                onSelectToggle={handleSelectToggle}
                onActionClicked={(id) => markActionClicked([id])}
              />
            ))}

          {/* Comments tab - only comment notifications */}
          {activeTab === "comments" &&
            comments.map(notification => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkRead={(id) => markAsRead([id])}
                onMarkUnread={(id) => markAsUnread([id])}
                onDelete={(id) => deleteSelected([id])}
                isSelected={selectedIds.has(notification.id) || selectAllMode}
                onSelectToggle={handleSelectToggle}
                onActionClicked={(id) => markActionClicked([id])}
              />
            ))}

          {hasMore && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2.5 border border-gray-200 bg-white hover:bg-gray-50 text-[#010D2C] hover:text-black font-bold rounded-lg shadow-sm transition-all text-sm duration-200"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      )}
      <BulkActionBar
        selectedCount={selectedIds.size}
        totalCount={total}
        selectAllMode={selectAllMode}
        onMarkRead={handleBulkMarkRead}
        onClear={handleBulkClear}
        onDeselectAll={handleDeselectAll}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Notifications"
        message={`Are you sure you want to delete ${deleteCount} notification${deleteCount !== 1 ? "s" : ""}? This action cannot be undone.`}
        confirmLabel="Yes, Delete"
        confirmStyle="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default NotificationsPage;
